import { CircularProgress, useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { BudgetPlanned, TrendEnum } from '../../../../constants/budgetPlanned';
import { useThemeContext } from '../../../../global-state/themeContext';
import { BudgetPlannedData } from '../../../../hooks';
import { useCurrencyTools } from '../../../../hooks/useCurrencyTools';
import { Container, Icon, Icons, Typography } from '../../../../stories/atoms';
import { Animation, Animations } from '../../../../stories/molecules';
import { muiTheme } from '../../../../theme';
import { homeTranslations } from '../../../../translations';
import { formatAmountWithSeparator, UserPermissions } from '../../../../utils';
import { computeBudgetPlannedData } from '../../../../utils/budgetPlanned';
import { InfoTooltipIcon } from '../infoTooltipIcon/InfoTooltipIcon';
import { RowFlexContainer } from '../../../../stories/atoms/containerV2/ContainerV2';
import {
  ItemContainer,
  SectionContentContainer,
  SectionHeaderContainer,
  StyledDivider,
  StyledSectionContainer,
  StyledTrendIcon,
  StyledTypography,
} from './BudgetPlannedContent.styles';

export const getBudgetAndPlannedPermissions = (userPermissions: UserPermissions) => {
  const canViewBudget = userPermissions.financialBudget.canRead;
  const canViewPlanned = userPermissions.financialPlanned.canRead;

  return {
    canViewBudget,
    canViewPlanned,
    showBudgetAndPlanned: canViewBudget || canViewPlanned,
  };
};

const { budgetPlanned: budgetPlannedTranslations, common } = homeTranslations;
type BudgetPlannedTranslation = keyof typeof budgetPlannedTranslations;

type CommonItemContainerProps = {
  label: string;
  value: number | string;
  trend: TrendEnum;
};

const CommonItemContainer: FC<CommonItemContainerProps> = ({ label, value, trend }) => {
  const { t } = useTranslation();
  const isMobileView = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  return (
    <ItemContainer>
      <Typography color="secondaryTextGray">
        {t(
          budgetPlannedTranslations[label as BudgetPlannedTranslation].key,
          budgetPlannedTranslations[label as BudgetPlannedTranslation].defaultValue,
        )}
      </Typography>
      <StyledTypography trend={trend} isMobileView={isMobileView}>
        {value}
      </StyledTypography>
    </ItemContainer>
  );
};

type SectionContentProps = {
  sectionLabel: string;
  icon: Icons;
  sectionData: BudgetPlanned;
  isCurrency: boolean;
};

const SectionContent: FC<SectionContentProps> = ({ sectionLabel, icon, sectionData, isCurrency }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const { trend, data, percentage, index, difference } = sectionData;
  const { getFormattedCurrency } = useCurrencyTools();

  return (
    <>
      <Container
        data-testid={`${sectionLabel}-header`}
        style={{ position: 'relative' }}
        wrap="nowrap"
        position="center"
        space="between"
        direction="horizontal"
      >
        <StyledTrendIcon trend={trend} data-testid="trend-icon" icon={Icons.ARROW} />
        <SectionHeaderContainer>
          <Icon
            color={mode === 'dark' ? 'white' : 'textGray'}
            margin={[0, 2, 0, 0]}
            data-testid={`${sectionLabel}-icon`}
            icon={icon}
          />
          <Typography type="button">
            {t(
              budgetPlannedTranslations[sectionLabel as BudgetPlannedTranslation].key,
              budgetPlannedTranslations[sectionLabel as BudgetPlannedTranslation].defaultValue,
            )}
          </Typography>
        </SectionHeaderContainer>
        <Typography>
          {isCurrency ? getFormattedCurrency(data, { showCurrencyCode: false }) : formatAmountWithSeparator(data)}
        </Typography>
      </Container>
      <SectionContentContainer data-testid={`${sectionLabel}-content`}>
        <CommonItemContainer label="differenceText" value={difference.toFixed(2)} trend={trend} />
        <CommonItemContainer label="indexText" value={index.toFixed()} trend={trend} />
        <CommonItemContainer
          label="trendText"
          value={`${trend === TrendEnum.UP ? '+' : '-'}${Math.abs(percentage).toFixed(2)}% ${
            sectionLabel === 'budget'
              ? t(common.thanBudget.key, common.thanBudget.defaultValue)
              : t(common.thanPlanned.key, common.thanPlanned.defaultValue)
          }`}
          trend={trend}
        />
      </SectionContentContainer>
    </>
  );
};

export type BudgetPlannedContentProps = {
  title: string;
  currentData: number | null;
  budgetPlanned: BudgetPlannedData | null;
  canViewBudget: boolean;
  canViewPlanned: boolean;
  budgetNoData?: boolean;
  plannedNoData?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  isEffectiveHours?: boolean;
  isCurrency?: boolean;
};

export const BudgetPlannedContent: FC<React.PropsWithChildren<BudgetPlannedContentProps>> = ({
  title,
  currentData,
  budgetPlanned,
  canViewBudget,
  canViewPlanned,
  budgetNoData,
  plannedNoData,
  isLoading = false,
  isError = false,
  isEffectiveHours = false,
  isCurrency = false,
}) => {
  const { t } = useTranslation();
  const { getFormattedCurrency } = useCurrencyTools();

  const {
    todayValue,
    budgetValue,
    plannedValue,
    budgetDiff,
    plannedDiff,
    budgetPercentage,
    plannedPercentage,
    budgetIndexValue,
    plannedIndexValue,
    budgetTrend,
    plannedTrend,
  } = computeBudgetPlannedData(currentData, budgetPlanned, isEffectiveHours);

  const budget = {
    trend: budgetTrend,
    data: budgetValue,
    percentage: budgetPercentage,
    index: budgetIndexValue,
    difference: budgetDiff,
  };

  const planned = {
    trend: plannedTrend,
    data: plannedValue,
    percentage: plannedPercentage,
    index: plannedIndexValue,
    difference: plannedDiff,
  };

  return (
    <>
      <Container margin={[0, 0, 2]}>
        <RowFlexContainer alignItems="center" gap={[1]}>
          <Typography type="h4">
            {t(budgetPlannedTranslations.title.key, budgetPlannedTranslations.title.defaultValue)}
          </Typography>
          <InfoTooltipIcon
            tooltipText={t(
              budgetPlannedTranslations.budgetTooltip.key,
              budgetPlannedTranslations.budgetTooltip.defaultValue,
            )}
            testId="budget-planned-info-icon"
          />
        </RowFlexContainer>
        <Typography>{title}</Typography>
      </Container>

      <StyledDivider data-testid="divider" />

      {isLoading && (
        <Container data-testid="loading-view" position="center" height="80%">
          <CircularProgress size={24} thickness={3} />
        </Container>
      )}

      {!isLoading && isError && (
        <Container data-testid="error-view" position="center" height="80%">
          <Animation
            title=""
            animation={Animations.SAD}
            size="small"
            errorText={t(budgetPlannedTranslations.error.key, budgetPlannedTranslations.error.defaultValue)}
          />
        </Container>
      )}

      {!isLoading && !isError && (
        <>
          <Typography>
            {t(budgetPlannedTranslations.currentText.key, budgetPlannedTranslations.currentText.defaultValue)}
          </Typography>
          <Typography type="button">
            {isCurrency
              ? getFormattedCurrency(todayValue, { showCurrencyCode: false })
              : formatAmountWithSeparator(todayValue)}
          </Typography>
        </>
      )}

      {!isLoading && !isError && canViewBudget && (
        <StyledSectionContainer data-testid="budget-wrapper" wrap="nowrap" padding={[2, 3]} margin={[2, 0, 3]}>
          {!budgetNoData && (
            <SectionContent
              sectionLabel="budget"
              icon={Icons.SALESEFFICIENCY}
              sectionData={budget}
              isCurrency={isCurrency}
            />
          )}
          {budgetNoData && (
            <Container data-testid="budget-no-data" position="center" height="100px">
              <Typography type="button" textAlign="center">
                {t(
                  budgetPlannedTranslations.noBudgetDataText.key,
                  budgetPlannedTranslations.noBudgetDataText.defaultValue,
                )}
              </Typography>
            </Container>
          )}
        </StyledSectionContainer>
      )}

      {!isLoading && !isError && canViewPlanned && (
        <StyledSectionContainer data-testid="planned-wrapper" wrap="nowrap" padding={[2, 3]}>
          {!plannedNoData && (
            <SectionContent
              sectionLabel="planned"
              icon={Icons.LIST}
              sectionData={planned}
              isCurrency={isCurrency}
            />
          )}
          {plannedNoData && (
            <Container data-testid="planned-no-data" position="center" height="100px">
              <Typography type="button" textAlign="center">
                {t(
                  budgetPlannedTranslations.noPlannedDataText.key,
                  budgetPlannedTranslations.noPlannedDataText.defaultValue,
                )}
              </Typography>
            </Container>
          )}
        </StyledSectionContainer>
      )}
    </>
  );
};
